'use client';

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useEmpresa } from '@/components/hooks/useEmpresa';
import { useUnsavedChanges } from '@/components/hooks/useUnsavedChanges';
import UnsavedChangesDialog from '@/components/pages/dialogs/UnsavedChangesDialog';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { getTourStyles } from '@/components/tour/tourStyles';
import {  Globe, Bell, Clock } from 'lucide-react';

import Joyride from 'react-joyride';
import CompanySettingsTourSteps from '@/components/tour/company/CompanySettingsTourSteps';
import { useTranslation } from 'react-i18next';
import { GLASS_CARD } from '@/components/ui/glass';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const INNER_ROW =
  'border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 rounded-lg';

export default function CompanySettings() {
  const { t } = useTranslation();
  const isMobile = window.innerWidth < 640;
  const id_empresa = localStorage.getItem('id_empresa');

  const { empresa, loadingEmpresa, saveEmpresa } = useEmpresa(
    id_empresa || undefined,
    'vista_empresa_detalle'
  );
  const [runTour, setRunTour] = useState(false);
  usePageHelp(() => setRunTour(true));
  useTourVisibility(runTour);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    privada: false,
    reservas_online_citas: true,
    permite_reservas_solo_clientes: false,
    dias_visibles_reserva: 14,
    notifica_cliente_email: true,
    notifica_trabajador_email: true,
    notifica_gestor_email: true,
    recordatorio_email_activo: true,
    recordatorio_email: 24,
  });

  useEffect(() => {
    if (empresa) {
      setForm({
        privada: empresa.privada ?? false,
        reservas_online_citas: empresa.reservas_online_citas ?? true,
        permite_reservas_solo_clientes:
          empresa.permite_reservas_solo_clientes ?? false,
        dias_visibles_reserva: empresa.dias_visibles_reserva ?? 14,
        notifica_cliente_email: empresa.notifica_cliente_email ?? true,
        notifica_trabajador_email: empresa.notifica_trabajador_email ?? true,
        notifica_gestor_email: empresa.notifica_gestor_email ?? true,
        recordatorio_email_activo: empresa.recordatorio_email_activo ?? true,
        recordatorio_email: empresa.recordatorio_email ?? 24,
      });
    }
  }, [empresa]);

  const hayCambios = useMemo(() => {
    if (!empresa) return false;
    return (
      form.privada !== (empresa.privada ?? false) ||
      form.reservas_online_citas !==
        (empresa.reservas_online_citas ?? true) ||
      form.permite_reservas_solo_clientes !==
        (empresa.permite_reservas_solo_clientes ?? false) ||
      form.dias_visibles_reserva !== (empresa.dias_visibles_reserva ?? 14) ||
      form.notifica_cliente_email !==
        (empresa.notifica_cliente_email ?? true) ||
      form.notifica_trabajador_email !==
        (empresa.notifica_trabajador_email ?? true) ||
      form.notifica_gestor_email !==
        (empresa.notifica_gestor_email ?? true) ||
      form.recordatorio_email_activo !==
        (empresa.recordatorio_email_activo ?? true) ||
      form.recordatorio_email !== (empresa.recordatorio_email ?? 24)
    );
  }, [empresa, form]);

  const { promptOpen, confirmLeave, cancelLeave } = useUnsavedChanges({
    when: hayCambios,
  });

  const handleSave = async () => {
    if (!empresa) return;

    const dias = Number(form.dias_visibles_reserva);

    if (!Number.isFinite(dias) || dias < 1 || dias > 60) {
      toast.error(t('companySettings.general.errorDays'));
      return;
    }

    if (form.recordatorio_email_activo) {
      if (
        !Number.isFinite(form.recordatorio_email) ||
        form.recordatorio_email < 1 ||
        form.recordatorio_email > 72
      ) {
        toast.error(t('companySettings.general.errorReminder'));
        return;
      }
    }

    setSaving(true);

    await saveEmpresa({
      privada: form.privada,
      reservas_online_citas: form.reservas_online_citas,
      permite_reservas_solo_clientes: form.permite_reservas_solo_clientes,
      dias_visibles_reserva: dias,
      notifica_cliente_email: form.notifica_cliente_email,
      notifica_trabajador_email: form.notifica_trabajador_email,
      notifica_gestor_email: form.notifica_gestor_email,
      recordatorio_email_activo: form.recordatorio_email_activo,
      recordatorio_email: form.recordatorio_email,
    });

    setSaving(false);
  };

  if (!id_empresa) return null;

  if (loadingEmpresa || !empresa) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600 dark:border-gray-400"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="w-full">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 tour-title">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('companySettings.title')}
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={CompanySettingsTourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('companySettings.tour.back'),
          close: t('companySettings.tour.close'),
          last: t('companySettings.tour.last'),
          next: t('companySettings.tour.next'),
          skip: t('companySettings.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* CONFIGURACIÓN GENERAL */}
      <Card className={`rounded-2xl ${GLASS_CARD} tour-card-general`}>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Globe className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companySettings.general.sectionTitle')}
            </h3>
          </div>

          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('companySettings.general.labelLanguage')}
              </Label>
              <input
                disabled
                value={empresa.idioma || 'Español'}
                className="w-full border rounded-md px-3 py-2 text-sm bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('companySettings.general.labelTimezone')}
              </Label>
              <input
                disabled
                value={empresa.zona_horaria || 'Europe/Madrid'}
                className="w-full border rounded-md px-3 py-2 text-sm bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-white/10"
              />
            </div>
          </motion.div>

          {/* Empresa privada */}
          <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3 mt-4 gap-4 tour-switch-privada`}>
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                {t('companySettings.general.privateTitle')}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('companySettings.general.privateDesc')}
              </p>
            </div>
            <Switch
              checked={form.privada}
              onCheckedChange={(checked) =>
                setForm((p) => ({ ...p, privada: checked }))
              }
            />
          </div>

          {/* Reservas online */}
          <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3 mt-2 gap-4 tour-switch-online`}>
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Reservas online
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Permite que los clientes reserven citas desde la web
              </p>
            </div>
            <Switch
              checked={form.reservas_online_citas}
              onCheckedChange={(checked) =>
                setForm((p) => ({ ...p, reservas_online_citas: checked }))
              }
            />
          </div>

          {/* Permitir reservas solo clientes */}
          <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3 mt-2 gap-4 tour-switch-solo-clientes`}>
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                {t('companySettings.general.clientsOnlyTitle')}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('companySettings.general.clientsOnlyDesc')}
              </p>
            </div>
            <Switch
              checked={form.permite_reservas_solo_clientes}
              onCheckedChange={(checked) =>
                setForm((p) => ({
                  ...p,
                  permite_reservas_solo_clientes: checked,
                }))
              }
            />
          </div>

          {/* Días visibles */}
          <div className={`${INNER_ROW} px-4 py-3 mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between tour-switch-dias`}>
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('companySettings.general.visibleDaysTitle')}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('companySettings.general.visibleDaysDesc')}
              </p>
            </div>

            <div className="flex items-center gap-2 sm:justify-end">
              <input
                type="number"
                min="1"
                max="60"
                value={form.dias_visibles_reserva}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    dias_visibles_reserva: Number(e.target.value),
                  }))
                }
                className="w-full max-w-[90px] border border-gray-300 dark:border-white/10 rounded-md px-3 py-2 text-sm text-center bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:border-[#103254] focus:ring-1 focus:ring-[#103254] transition"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('companySettings.general.visibleDaysUnit')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* NOTIFICACIONES EMAIL */}
      <Card className={`rounded-2xl ${GLASS_CARD} tour-card-notifications`}>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Bell className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companySettings.notifications.sectionTitle')}
            </h3>
          </div>

          <div className="flex flex-col gap-4">
            {[
              {
                key: 'notifica_cliente_email',
                label: t('companySettings.notifications.clientLabel'),
                desc: t('companySettings.notifications.clientDesc'),
              },
              {
                key: 'notifica_trabajador_email',
                label: t('companySettings.notifications.workerLabel'),
                desc: t('companySettings.notifications.workerDesc'),
              },
              {
                key: 'notifica_gestor_email',
                label: t('companySettings.notifications.managerLabel'),
                desc: t('companySettings.notifications.managerDesc'),
              },
            ].map(({ key, label, desc }) => (
              <div
                key={key}
                className={`flex items-center justify-between ${INNER_ROW} px-4 py-3 hover:shadow-sm transition-shadow gap-4 tour-switch-${key}`}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {desc}
                  </span>
                </div>
                <Switch
                  checked={!!form[key as keyof typeof form]}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, [key]: checked }))
                  }
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* RECORDATORIOS EMAIL */}
      <Card className={`rounded-2xl ${GLASS_CARD} tour-card-reminders`}>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Clock className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('companySettings.reminders.sectionTitle')}
            </h3>
          </div>

          <div className={`flex items-center justify-between ${INNER_ROW} px-4 py-3 gap-4 tour-switch-recordatorio-activo`}>
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('companySettings.reminders.emailTitle')}
              </span>
            </div>

            <Switch
              checked={form.recordatorio_email_activo}
              onCheckedChange={(checked) =>
                setForm((prev) => ({
                  ...prev,
                  recordatorio_email_activo: checked,
                }))
              }
            />
          </div>

          <div className={`${INNER_ROW} px-4 py-3 mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between tour-switch-recordatorio-horas`}>
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {t('companySettings.reminders.smsTitle')}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('companySettings.reminders.smsComingSoon')}
              </p>
            </div>

            <Switch checked={false} disabled />
          </div>

          {form.recordatorio_email_activo && (
            <div className={`${INNER_ROW} px-4 py-3 mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`}>
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('companySettings.reminders.hoursTitle')}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('companySettings.reminders.hoursDesc')}
                </p>
              </div>

              <div className="flex items-center gap-2 sm:justify-end">
                <input
                  type="number"
                  min="1"
                  max="72"
                  value={form.recordatorio_email}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      recordatorio_email: Number(e.target.value),
                    }))
                  }
                  className="w-full max-w-[90px] border border-gray-300 dark:border-white/10 rounded-md px-3 py-2 text-sm text-center bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:border-[#103254] focus:ring-1 focus:ring-[#103254] transition"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('companySettings.reminders.hoursUnit')}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* BOTÓN GUARDAR */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#103254] hover:bg-[#0d2742] text-white rounded-lg px-5 py-2 font-medium shadow-md transition-colors tour-save-btn"
          >
            {saving
              ? t('companySettings.saving')
              : t('companySettings.saveBtn')}
          </Button>
        </CardContent>
      </Card>

      <UnsavedChangesDialog
        open={promptOpen}
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />

    </motion.div>
  );
}